// ============================================================
// File: src/components/AskAssistant.js
// FINAL — With tenant/user-based chat reset (SAFE update)
// ============================================================

import React, { useState, useRef, useEffect, useContext } from "react";
import axiosClient from "../api/axiosClient";
import "../styles/askAssistant.css";
import { AuthContext } from "../context/AuthContext";

function AskAssistant({ onClose }) {
  const { user, activeScope } = useContext(AuthContext);

  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // ======================================================
  // 🔹 RESET CHAT WHEN USER OR TENANT CHANGES
  // ======================================================
  useEffect(() => {
    setResponse("");      // Clear existing answer
    setQuery("");         // Clear text box
  }, [user?.id, activeScope?.tenant_id]);

  // 🔹 Safe message extraction
  const extractMessage = (res) => {
    try {
      if (!res) return null;
      const d = res.data;
      const paths = [
        () => d?.data?.message,
        () => d?.message,
        () => d?.data?.data?.message,
        () => d?.data,
      ];
      for (let fn of paths) {
        const val = fn();
        if (val) return typeof val === "object" ? JSON.stringify(val) : String(val);
      }
      return null;
    } catch {
      return null;
    }
  };

  // 🔹 Handle text query
  const handleAsk = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      alert("Please enter your question.");
      return;
    }

    setLoading(true);
    setResponse("");

    try {
      const res = await axiosClient.post("/ai/query_ai", { query: query.trim() });
      const msg = extractMessage(res);
      setResponse(msg || "⚠️ AI query processed successfully (no message found).");
    } catch (err) {
      console.error("❌ AI Query Error:", err);
      setResponse("An error occurred while processing your query.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle voice input
  const handleVoice = async () => {
    try {
      if (!recording) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        audioChunksRef.current = [];
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) audioChunksRef.current.push(event.data);
        };

        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          const formData = new FormData();
          formData.append("file", audioBlob, "voice.webm");

          setLoading(true);
          setResponse("");

          try {
            const res = await axiosClient.post("/ai/voice_query", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            const msg = extractMessage(res);
            setResponse(msg || "Voice processed but no clear response.");
          } catch (err) {
            console.error("❌ Voice Query Error:", err);
            setResponse("Voice processing failed. Try again.");
          } finally {
            setLoading(false);
          }
        };

        mediaRecorder.start();
        setRecording(true);
      } else {
        mediaRecorderRef.current.stop();
        setRecording(false);
      }
    } catch (err) {
      console.error("🎙️ Voice Capture Error:", err);
      alert("Microphone access denied or unavailable.");
    }
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div className="assistant-drawer" onClick={stopPropagation}>
      <div className="assistant-header">
        <h5>🧠 Ask Your Assistant</h5>
        <button
          className="btn btn-danger btn-sm"
          onClick={onClose}
          style={{ fontWeight: "500", borderRadius: "6px" }}
        >
          ✕ Close
        </button>
      </div>

      <div className="assistant-body">
        <form onSubmit={handleAsk}>
          <textarea
            className="form-control"
            placeholder="Ask about tasks, team, or project updates..."
            rows="3"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          ></textarea>

          <div className="d-flex gap-2 mt-2 align-items-center">
            <button
              className="btn gradient-btn flex-grow-1"
              type="submit"
              disabled={loading}
            >
              {loading ? "Processing..." : "Ask"}
            </button>

            <button
              type="button"
              className={`btn ${recording ? "btn-danger" : "btn-outline-primary"}`}
              onClick={handleVoice}
              title={recording ? "Stop recording" : "Start recording"}
            >
              {recording ? "🛑 Stop" : "🎙️ Voice"}
            </button>
          </div>

          {recording && (
            <div className="waveform mt-3">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="bar"></div>
              ))}
              <span className="listening-text">Listening...</span>
            </div>
          )}
        </form>

        {response && (
          <div className="assistant-response mt-3 p-3 rounded shadow-sm bg-light">
            <strong>🤖 Response:</strong>
            <div
              className="mt-2"
              style={{
                whiteSpace: "pre-line",
                fontSize: "0.95rem",
                lineHeight: "1.5",
                color: "#333",
              }}
            >
              {response}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AskAssistant;
